import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { logout } from '../store/authSlice';
import toast from 'react-hot-toast';
import { AlertCircle } from 'lucide-react';
import { authAPI } from '../api';

const formatTime = (timeInSeconds: number) => {
  const m = Math.floor(timeInSeconds / 60);
  const s = timeInSeconds % 60;
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
};

const IdleTimeoutHandler = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(300);

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const isModalOpenRef = useRef(false);

  useEffect(() => {
    isModalOpenRef.current = showModal;
  }, [showModal]);

  const handleIdleTimeout = () => {
    if (isAuthenticated) {
      if (intervalId.current) clearInterval(intervalId.current);
      if (timeoutId.current) clearTimeout(timeoutId.current);
      setShowModal(false);
      authAPI.logout().catch(() => {});
      dispatch(logout());
      toast.error('Session expired due to inactivity. Please login again.');
      navigate('/login');
    }
  };

  const startWarningCountdown = () => {
    setShowModal(true);
    setRemainingTime(900); // for 15 mins
    
    if (intervalId.current) clearInterval(intervalId.current);
    
    intervalId.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (intervalId.current) clearInterval(intervalId.current);
          handleIdleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetTimer = () => {
    if (isModalOpenRef.current) return; // Ignore background activity when modal is open

    if (timeoutId.current) clearTimeout(timeoutId.current);
    if (intervalId.current) clearInterval(intervalId.current);
    
    if (isAuthenticated) {
      // 1 hr 45 mins until warning modal
      timeoutId.current = setTimeout(startWarningCountdown, 6300000);
    }
  };

  const handleStayIn = () => {
    setShowModal(false);
    if (intervalId.current) clearInterval(intervalId.current);
    resetTimer(); // restart the 2-hour timer
  };

  useEffect(() => {
    if (isAuthenticated) {
      resetTimer();

      const events = ['click', 'keydown'];
      
      const handleUserActivity = () => {
        resetTimer();
      };

      events.forEach((event) => {
        window.addEventListener(event, handleUserActivity);
      });

      return () => {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        if (intervalId.current) clearInterval(intervalId.current);
        events.forEach((event) => {
          window.removeEventListener(event, handleUserActivity);
        });
      };
    } else {
      if (timeoutId.current) clearTimeout(timeoutId.current);
      if (intervalId.current) clearInterval(intervalId.current);
      setShowModal(false);
    }
  }, [isAuthenticated, location.pathname]);

  if (!showModal) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '400px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        textAlign: 'center',
        animation: 'fadeIn 0.3s ease-out'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '50%' }}>
            <AlertCircle size={40} color="#ef4444" />
          </div>
        </div>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f172a', marginBottom: '8px' }}>Are you still there?</h2>
        <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '15px' }}>
          Your session will expire soon due to inactivity.
        </p>
        
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button 
            onClick={handleIdleTimeout}
            style={{
              padding: '12px 20px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: '#ef4444',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
              flex: 1
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#b62121ff')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#ef4444')}
          >
            Logout ({formatTime(remainingTime)})
          </button>
          <button 
            onClick={handleStayIn}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              background: '#14b8a6',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
              flex: 1
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = '#0d9488')}
            onMouseOut={(e) => (e.currentTarget.style.background = '#14b8a6')}
          >
            Stay In
          </button>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default IdleTimeoutHandler;
